import { useDebouncedCallback } from "use-debounce";
import { InputText } from "primereact/inputtext";

import { SettingsKey, kToolbarHeight } from "models";
import { useAppSelector, useAppDispatch, setOption } from "utils/store";
import { saveTemplate } from "utils/api";
import { SidebarMenu } from "utils/ui";

import { LayoutWrapper } from "../layout";

const DEFAULT_TITLE = "Contents";
const skeletonClass = "h-[1.2rem] bg-[var(--ion-color-light-shade)] rounded";

/** Settings panel for the Contents page — currently just the page title. */
const ContentsMenu = () => {
  const dispatch = useAppDispatch();
  const options = useAppSelector((state) => state.templateBuilderSlice.defaultOptions);
  const disabled = useAppSelector((state) => state.templateBuilderSlice.loading);

  const doDebouncedSave = useDebouncedCallback(async () => dispatch(saveTemplate()), 3000, {
    leading: false,
    trailing: true,
  });

  const title = (options[SettingsKey.ContentsPageTitle]?.value as string) ?? DEFAULT_TITLE;

  const onTitleChange = (value: string) => {
    dispatch(
      setOption({
        key: SettingsKey.ContentsPageTitle,
        option: { ...options[SettingsKey.ContentsPageTitle], value } as any,
      })
    );
    doDebouncedSave();
  };

  return (
    <SidebarMenu.Wrapper title="Contents" style={{ height: `calc(100vh - 60px - ${kToolbarHeight}px)` }}>
      <SidebarMenu.Heading title="Page Title" />
      <SidebarMenu.Description>The heading shown at the top of the contents page.</SidebarMenu.Description>
      <InputText
        className="w-full mt-2"
        value={title}
        disabled={disabled}
        onChange={(e) => onTitleChange(e.target.value)}
      />
      <SidebarMenu.Divider />
      <SidebarMenu.Description>The list of sections is generated automatically when the report is produced.</SidebarMenu.Description>
    </SidebarMenu.Wrapper>
  );
};

/** A4 skeleton preview: the title plus placeholder contents rows. */
const ContentsPreview = () => {
  const options = useAppSelector((state) => state.templateBuilderSlice.defaultOptions);
  const title = (options[SettingsKey.ContentsPageTitle]?.value as string) ?? DEFAULT_TITLE;

  return (
    <div className="pt-2 border-b-2 border-[var(--ion-color-dark)] pb-2 w-full min-h-10">
      <h1 style={{ marginBottom: "1.5rem" }}>{title || DEFAULT_TITLE}</h1>

      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="flex flex-row items-center"
          style={{ marginBottom: "1rem", gap: "0.75rem" }}
        >
          <div className={skeletonClass} style={{ flex: 1 }}></div>
          <div className={skeletonClass} style={{ width: "2.5rem" }}></div>
        </div>
      ))}
    </div>
  );
};

function ContentsTemplate() {
  return <LayoutWrapper menu={<ContentsMenu />} preview={<ContentsPreview />} />;
}

export default ContentsTemplate;
